import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format, startOfMonth, addMonths, isBefore } from "date-fns";
import apiClient from "@/utils/httpClient";
import { ExpensePayableItem, ExpensePayableApiResponse, InvoiceApiResponse, Invoice, CashflowKPIs as ApiCashflowKPIs, BusinessOverviewKPIs, CashflowData, CashflowResponse } from "@blinkbooks/types";
import { CashFlowKPIs, CashFlowChartData, CategoryBreakdownData, Granularity } from "./interfaces";
import { DateFilterKey, resolveDateFilter, DATE_FILTER_OPTIONS } from "@/utils/dateFilters";
import { DateFilter } from "@/components/ui/DateFilter";
import { KPIRow } from "./KPIRow";
import { CashFlowChart } from "./CashFlowChart";
import BalanceForecastChart from "./BalanceForecastChart";
import { CategoryBreakdownCharts } from "./CategoryBreakdownCharts";

/** Get list of "MM-YYYY" month keys spanning from dateFrom to dateTo */
function getMonthKeys(dateFrom: Date, dateTo: Date): string[] {
  const months: string[] = [];
  let current = startOfMonth(dateFrom);
  const end = startOfMonth(dateTo);
  while (isBefore(current, end) || current.getTime() === end.getTime()) {
    months.push(format(current, 'MM-yyyy'));
    current = addMonths(current, 1);
  }
  return months;
}


interface CashflowForecastProps {
  dateFilterKey: DateFilterKey;
  dateCustomFrom?: Date;
  dateCustomTo?: Date;
  onDateFilterChange: (key: DateFilterKey | '', from?: Date, to?: Date) => void;
}

const CashflowForecast = ({ dateFilterKey, dateCustomFrom, dateCustomTo, onDateFilterChange }: CashflowForecastProps) => {
  const [kpis, setKpis] = useState<ApiCashflowKPIs | null>(null);
  const [businessKpis, setBusinessKpis] = useState<BusinessOverviewKPIs | null>(null);
  const [cashflowChartData, setCashflowChartData] = useState<CashflowData[]>([]);
  const [unpaidBillsExpenses, setUnpaidBillsExpenses] = useState<ExpensePayableItem[]>([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState<Invoice[]>([]);
  // Fixed 18-month window for balance chart — not re-fetched on date filter change
  const [balanceBE, setBalanceBE] = useState<ExpensePayableItem[]>([]);
  const [balanceInv, setBalanceInv] = useState<Invoice[]>([]);
  const [balanceChartLoading, setBalanceChartLoading] = useState(true);
  const [categoryData, setCategoryData] = useState<CategoryBreakdownData[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(true);

  const resolvedRange = useMemo(() => {
    const range = resolveDateFilter(dateFilterKey, dateCustomFrom, dateCustomTo);
    if (range) return range;
    // Fallback: last 30 days
    const to = new Date();
    const from = new Date(to);
    from.setDate(to.getDate() - 30);
    return { startDate: format(from, 'yyyy-MM-dd'), endDate: format(to, 'yyyy-MM-dd') };
  }, [dateFilterKey, dateCustomFrom, dateCustomTo]);

  const granularity = useMemo((): Granularity => {
    // Backend returns monthly aggregates only, so biweekly/daily bucketing
    // breaks for any range the API covers with month-level data.
    return 'monthly';
  }, []);

  const rangeLabel = DATE_FILTER_OPTIONS.find(o => o.key === dateFilterKey)?.label ?? 'Selected period';

  const periodMonths = useMemo(() => {
    const days = (new Date(resolvedRange.endDate).getTime() - new Date(resolvedRange.startDate).getTime()) / 86_400_000;
    return Math.max(1, Math.round(days / 30.44));
  }, [resolvedRange]);

  // Fetch KPIs and business overview for the selected date range
  useEffect(() => {
    const { startDate, endDate } = resolvedRange;
    Promise.all([
      apiClient.get<ApiCashflowKPIs>('/cashflow/kpis', { startDate, endDate }),
      apiClient.get<BusinessOverviewKPIs>('/kpi/business-overview', { startDate, endDate }),
    ])
      .then(([kpiData, bizData]) => {
        setKpis(kpiData || null);
        setBusinessKpis(bizData || null);
      })
      .catch(err => console.error('Error fetching KPIs:', err));
  }, [resolvedRange]);

  // Fetch cashflow chart data + unpaid items for forecast — re-fetched on date filter change
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { startDate, endDate } = resolvedRange;
        const startMonth = format(new Date(startDate), "MM-yyyy");
        const endMonth = format(new Date(endDate), "MM-yyyy");

        const [cashflowRes, unpaidBEResponse, unpaidInvResponse] = await Promise.all([
          apiClient.getRaw<{ success: boolean; data: CashflowResponse }>(`/cashflow?startMonth=${startMonth}&endMonth=${endMonth}`),
          apiClient.getRaw<ExpensePayableApiResponse>(`/expenses-payable?status=unpaid&limit=10000`),
          apiClient.getRaw<InvoiceApiResponse>(`/invoices?status=unpaid,partially_paid&limit=10000`),
        ]);

        setCashflowChartData(cashflowRes?.data?.actual || []);
        setUnpaidBillsExpenses(
          (unpaidBEResponse?.data || []).filter(item => item.source_type === 'bill' || item.source_type === 'expense')
        );
        setUnpaidInvoices(unpaidInvResponse?.data || []);
      } catch (error) {
        console.error('Error fetching cashflow data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [resolvedRange]);

  // Fetch 18-month fixed window for the balance chart — runs once, independent of date filter
  useEffect(() => {
    const fetchBalanceData = async () => {
      try {
        setBalanceChartLoading(true);
        const endDate = format(new Date(), "yyyy-MM-dd");
        const startDate = format(addMonths(new Date(), -18), "yyyy-MM-dd");
        const [beRes, invRes] = await Promise.all([
          apiClient.getRaw<ExpensePayableApiResponse>(
            `/expenses-payable?dateFrom=${startDate}&dateTo=${endDate}&limit=10000`
          ),
          apiClient.getRaw<InvoiceApiResponse>(
            `/invoices?dateFrom=${startDate}&dateTo=${endDate}&limit=10000`
          ),
        ]);
        setBalanceBE(
          (beRes?.data || []).filter(
            (item) => item.source_type === "bill" || item.source_type === "expense"
          )
        );
        setBalanceInv(invRes?.data || []);
      } catch (err) {
        console.error("Error fetching balance chart data:", err);
      } finally {
        setBalanceChartLoading(false);
      }
    };
    fetchBalanceData();
  }, []); // intentionally runs once — not tied to date filter

  // Fetch category breakdown for each month in the selected range
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true);
        const dateFrom = new Date(resolvedRange.startDate);
        const dateTo = new Date(resolvedRange.endDate);
        const months = getMonthKeys(dateFrom, dateTo);

        if (months.length === 0) {
          setCategoryData([]);
          setCategoryLoading(false);
          return;
        }

        const categoryPromises = months.map(month =>
          apiClient.get<CategoryBreakdownData>(`/cashflow/category-breakdown/${month}`)
            .then(res => res ? { ...res, month } : null)
            .catch(() => null)
        );

        const results = await Promise.all(categoryPromises);
        setCategoryData(results.filter((r): r is CategoryBreakdownData => r !== null && r.categories?.length > 0));
      } catch (error) {
        console.error('Error fetching category breakdown:', error);
      } finally {
        setCategoryLoading(false);
      }
    };

    fetchCategories();
  }, [resolvedRange]);

  // Convert /cashflow endpoint data to chart format, filling gaps with zeros
  const actualData = useMemo((): CashFlowChartData[] => {
    const dataMap = new Map(cashflowChartData.map(row => [row.month, row]));
    const start = startOfMonth(new Date(resolvedRange.startDate));
    const end = startOfMonth(new Date(resolvedRange.endDate));
    const months: CashFlowChartData[] = [];
    let cur = start;
    while (!isBefore(end, cur)) {
      const key = format(cur, 'MM-yyyy');
      const row = dataMap.get(key);
      months.push({
        month: format(cur, 'MMM yyyy'),
        inflows_usd: row?.inflows_usd ?? 0,
        outflows_usd: row?.outflows_usd ?? 0,
        net_flow_usd: row?.net_flow_usd ?? 0,
        cumulative_balance_usd: row?.cumulative_balance_usd ?? 0,
      });
      cur = addMonths(cur, 1);
    }
    return months;
  }, [cashflowChartData, resolvedRange]);

  // Compute display KPIs — all values sourced from server responses to match Financial KPI Panel
  const computedKpis = useMemo((): CashFlowKPIs | null => {
    if (!kpis) return null;

    const hasBankData = kpis.hasBankData;
    const cashCollectedValue = businessKpis?.revenueMTD.value ?? 0;
    const cashExpensesValue = businessKpis?.expensesTotal.value ?? 0;
    const operatingCashFlowValue = businessKpis?.netProfit.value ?? (cashCollectedValue - cashExpensesValue);

    return {
      hasBankData,
      cashOnHand: kpis.cashOnHand,
      netCashFlowMonth: {
        name: 'Net Cash Flow',
        formula: `All bank inflows − outflows · All Time · Live`,
        value: kpis.netCashFlowMonth.value,
        currency: 'USD',
      },
      cashCollected: {
        name: 'Cash Inflows',
        formula: `Operational bank inflows · excludes transfers, loans & financing · ${rangeLabel}`,
        value: cashCollectedValue,
        currency: 'USD',
      },
      cashExpenses: {
        name: 'Cash Outflows',
        formula: `Operational bank outflows · vendor payments, payroll, expenses, taxes · excludes transfers & investments · ${rangeLabel}`,
        value: cashExpensesValue,
        currency: 'USD',
      },
      operatingCashFlow: {
        name: 'Operating Cash Flow',
        formula: `Cash Inflows − Cash Outflows · ${rangeLabel}`,
        value: operatingCashFlowValue,
        currency: 'USD',
      },
      burnRate: {
        name: 'Monthly Burn Rate',
        formula: `Σ(abs(negative monthly net cash flows)) ÷ ${periodMonths} ${periodMonths === 1 ? 'month' : 'months'} · ${rangeLabel}`,
        value: kpis.burnRate.value,
        currency: 'USD',
        unit: '/mo',
      },
    };
  }, [kpis, businessKpis, periodMonths, rangeLabel]);

  if (loading) {
    return (
      <Card className="m-6 shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle>Cashflow & Forecast</CardTitle>
            <DateFilter
              value={dateFilterKey}
              customFrom={dateCustomFrom}
              customTo={dateCustomTo}
              onChange={onDateFilterChange}
              label=""
              width="w-[160px]"
              showEmpty={false}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-96">
            <p className="text-muted-foreground">Loading cash flow data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="m-6 shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle>Cashflow & Forecast</CardTitle>
          <DateFilter
            value={dateFilterKey}
            customFrom={dateCustomFrom}
            customTo={dateCustomTo}
            onChange={onDateFilterChange}
            label=""
            width="w-[160px]"
            showEmpty={false}
          />
        </div>
      </CardHeader>
      <CardContent>
        {computedKpis && <KPIRow kpis={computedKpis} rangeLabel={rangeLabel} />}

        <CashFlowChart actualData={actualData} granularity={granularity} dateRange={resolvedRange} loading={loading} />

        <BalanceForecastChart
          historicalBE={balanceBE}
          historicalInv={balanceInv}
          unpaidBE={unpaidBillsExpenses}
          unpaidInv={unpaidInvoices}
          cashOnHand={computedKpis?.cashOnHand.value ?? 0}
          loading={balanceChartLoading}
        />

        <CategoryBreakdownCharts data={categoryData} loading={categoryLoading} rangeLabel={rangeLabel} />
      </CardContent>
    </Card>
  );
};

export default CashflowForecast;
